/**
 * Promo Compatibility Helper
 * ──────────────────────────
 * Resolves which storefront promos should actually render when multiple
 * campaigns from /api/storefront/active-promos overlap on a customer's
 * cart/PDP context.
 *
 * Why this exists:
 *   Shopify's `combinesWith.productDiscounts` flag controls whether two
 *   product-discount promos can stack at checkout. When it's false on
 *   either of two overlapping promos, only ONE applies — but the theme
 *   was previously painting all matching banners regardless. Customers
 *   saw two promos and only got one. This helper picks the winner so the
 *   visible banner matches what'll actually apply.
 *
 * How it picks the winner:
 *   For each pair of promos that (a) share at least one product ID in
 *   their eligibility/qualifying lists AND (b) have `productDiscounts:
 *   false` on either side, the helper computes projected savings on the
 *   current cart and keeps the higher one. Tie-break: lower campaignId
 *   (older campaign) wins for stability.
 *
 * Public API:
 *   window.PromoCompat.ready          → Promise<{byId, raw, cart}>
 *   window.PromoCompat.isApplicable(campaignId) → Promise<boolean>
 *
 * Failure mode: fail-OPEN. If the helper crashes (network down, bad
 * data), every campaign is treated as applicable. Better to show a
 * banner than silently hide one.
 *
 * Loaded BEFORE bxgy-promo.js / min-qty-promo.js / bundle-promo.js so
 * window.PromoCompat exists when they bootstrap. Each consumer awaits
 * `PromoCompat.ready` before deciding whether to render its card/banner.
 */
(function () {
  'use strict';

  var BR_CONNECT_BASE = window.__beautyRepConnectBase || 'https://beautyrepconnect.com';
  var ACTIVE_PROMOS_PATH = '/api/storefront/active-promos';

  function isPreviewContext() {
    try {
      if (window.Shopify && window.Shopify.designMode === true) return true;
      var url = new URL(window.location.href);
      if (url.searchParams.has('preview_theme_id')) return true;
      if (/shopifypreview\.com$/.test(url.hostname)) return true;
    } catch (e) {}
    return false;
  }

  function fetchJson(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (res) {
      if (!res.ok) throw new Error(url + ' → ' + res.status);
      return res.json();
    });
  }

  function gidToNumericId(gid) {
    if (!gid || typeof gid !== 'string') return null;
    var m = gid.match(/(\d+)$/);
    return m ? m[1] : null;
  }

  function productIdsForPromo(p) {
    if (p.type === 'bxgy_1to1_scaling') {
      return [].concat(p.qualifyingProductIds || [], p.freeProductIds || []);
    }
    if (p.type === 'min_quantity_percentage') return p.eligibleProductIds || [];
    if (p.type === 'bundle_percentage') return p.bundleProductIds || [];
    return [];
  }

  /**
   * Projected savings (cents) on the current cart. Used as the tiebreaker
   * when two incompatible promos overlap. Below-threshold returns 0 — the
   * promo isn't actually saving the customer anything yet.
   */
  function projectedSavingsCents(promo, cart) {
    var items = (cart && cart.items) || [];
    if (!items.length) return 0;

    if (promo.type === 'bxgy_1to1_scaling') {
      var qIds = (promo.qualifyingProductIds || []).map(gidToNumericId).filter(Boolean);
      var fIds = (promo.freeProductIds || []).map(gidToNumericId).filter(Boolean);
      var qualQty = 0;
      var freeUnitCents = 0;
      for (var i = 0; i < items.length; i++) {
        var l = items[i];
        var pid = String(l.product_id);
        if (qIds.indexOf(pid) !== -1) qualQty += l.quantity || 0;
        // Use the highest free-product original_price seen as the per-unit
        // retail proxy. The free product's WSP is what the customer would
        // otherwise pay, so it's a defensible "savings per freebie" basis.
        if (fIds.indexOf(pid) !== -1 && (l.original_price || 0) > freeUnitCents) {
          freeUnitCents = l.original_price;
        }
      }
      var threshold = promo.threshold || 1;
      if (qualQty < threshold) return 0;
      // 1:1 scaling: each qualifying unit yields 1 free freebie. If the
      // free product isn't yet in the cart we don't know its price, so
      // fall back to the qualifying product's own original_price as a
      // rough proxy (caps the savings to "buying SCMS = freebie value
      // ≤ SCMS price" — conservative, won't overstate the promo).
      if (!freeUnitCents) {
        for (var j = 0; j < items.length; j++) {
          var ll = items[j];
          if (qIds.indexOf(String(ll.product_id)) !== -1) {
            freeUnitCents = ll.original_price || 0;
            break;
          }
        }
      }
      return qualQty * freeUnitCents;
    }

    if (promo.type === 'min_quantity_percentage') {
      var eIds = (promo.eligibleProductIds || []).map(gidToNumericId).filter(Boolean);
      var eligQty = 0;
      var eligSubtotalCents = 0;
      for (var k = 0; k < items.length; k++) {
        var li = items[k];
        if (eIds.indexOf(String(li.product_id)) !== -1) {
          eligQty += li.quantity || 0;
          eligSubtotalCents += (li.original_price || 0) * (li.quantity || 0);
        }
      }
      if (eligQty < (promo.minQuantity || 1)) return 0;
      return Math.round(eligSubtotalCents * (promo.percentage || 0));
    }

    if (promo.type === 'bundle_percentage') {
      var bIds = (promo.bundleProductIds || []).map(gidToNumericId).filter(Boolean);
      var inCartIds = {};
      var bundleSubtotalCents = 0;
      for (var m = 0; m < items.length; m++) {
        var lm = items[m];
        var pidM = String(lm.product_id);
        if (bIds.indexOf(pidM) !== -1) {
          inCartIds[pidM] = true;
          bundleSubtotalCents += (lm.original_price || 0) * (lm.quantity || 0);
        }
      }
      // Bundle requires ALL listed products in cart to fire.
      if (Object.keys(inCartIds).length < bIds.length) return 0;
      return Math.round(bundleSubtotalCents * (promo.percentage || 0));
    }

    return 0;
  }

  /**
   * Walk all pairs. For each pair:
   *   - skip if either is `combinesWith.productDiscounts: true` (compatible)
   *   - skip if no product overlap in eligibility lists (no conflict)
   *   - else, mark the lower-savings one as `applicable: false`
   *     (tie → keep the lower campaignId, drop the higher)
   *
   * O(n²) over promos but n is tiny (5-10 active campaigns max in practice),
   * so we don't bother with cleverer grouping. Predictable + auditable wins.
   */
  function decideApplicable(promos, cart) {
    var enriched = promos.map(function (p) {
      var ids = productIdsForPromo(p).map(gidToNumericId).filter(Boolean);
      var set = {};
      ids.forEach(function (id) { set[id] = true; });
      return {
        promo: p,
        campaignId: p.campaignId,
        productSet: set,
        savings: projectedSavingsCents(p, cart),
        applicable: true,
        lostTo: null,
      };
    });

    for (var i = 0; i < enriched.length; i++) {
      var a = enriched[i];
      if (!a.applicable) continue;
      for (var j = i + 1; j < enriched.length; j++) {
        var b = enriched[j];
        if (!b.applicable) continue;

        // Compatible if BOTH allow combining with other product discounts.
        var aProd = a.promo.combinesWith && a.promo.combinesWith.productDiscounts === true;
        var bProd = b.promo.combinesWith && b.promo.combinesWith.productDiscounts === true;
        if (aProd && bProd) continue;

        // Overlap: any shared product in eligibility lists.
        var overlap = false;
        for (var pid in a.productSet) {
          if (b.productSet[pid]) { overlap = true; break; }
        }
        if (!overlap) continue;

        // Winner: higher savings. Tie: lower campaignId.
        var aWins = a.savings > b.savings ||
                    (a.savings === b.savings && a.campaignId < b.campaignId);
        if (aWins) {
          b.applicable = false;
          b.lostTo = a.campaignId;
        } else {
          a.applicable = false;
          a.lostTo = b.campaignId;
          break; // a is out of the running
        }
      }
    }
    return enriched;
  }

  // Ready Promise — kicks off both fetches in parallel at script load.
  // Each consumer awaits this; we don't redo the work.
  var readyPromise = Promise.all([
    fetchJson(BR_CONNECT_BASE + ACTIVE_PROMOS_PATH + (isPreviewContext() ? '?include_draft=1' : '')),
    fetchJson('/cart.js').catch(function () { return { items: [] }; }),
  ])
    .then(function (results) {
      var data = results[0] || {};
      var cart = results[1] || { items: [] };
      var promos = data.promos || [];
      var decisions = decideApplicable(promos, cart);
      var byId = {};
      decisions.forEach(function (d) {
        byId[d.campaignId] = {
          applicable: d.applicable,
          projectedSavingsCents: d.savings,
          lostTo: d.lostTo,
        };
      });
      // Debug-friendly: surface the full decision array on window so it's
      // inspectable from devtools without re-fetching.
      window.PromoCompat._lastDecisions = decisions;
      return { byId: byId, raw: data, cart: cart };
    })
    .catch(function (err) {
      console.warn('[promo-compat] init failed (failing open):', err);
      return { byId: {}, raw: null, cart: null };
    });

  window.PromoCompat = {
    ready: readyPromise,
    /**
     * Resolves to true when the campaign should render, false when it lost
     * to an incompatible sibling. Unknown campaignIds resolve to true
     * (fail-open). Helper crash also resolves to true.
     */
    isApplicable: function (campaignId) {
      return readyPromise
        .then(function (r) {
          var d = r.byId[campaignId];
          return d ? d.applicable : true;
        })
        .catch(function () { return true; });
    },
    _lastDecisions: null,
  };
})();
